import create from 'zustand'
import { combine } from 'zustand/middleware'

/**
 * A single object hit by the debug raycast, in front-to-back order (the first
 * entry is the top-most object under the cursor). Used by the "Scene Element
 * Debug" dev tool to reveal what actually sits on top of a clicked spot –
 * including invisible/transparent meshes that intercept pointer events.
 */
export interface DebugHit {
  /** Best-effort human label (own name, a userData id, or the object type). */
  label: string
  /** The object's own `name` (often empty for generated meshes). */
  name: string
  /** Three.js object type, e.g. `Mesh`, `Line2`, `Group`. */
  type: string
  /** Shortened uuid for disambiguation. */
  uuid: string
  /** Ray distance from the camera (front-to-back ordering). */
  distance: number
  /** Whether the object and all its ancestors are visible. */
  visible: boolean
  /** World-space intersection point. */
  point: [number, number, number]
  /** Material opacity, when the object has a material. */
  opacity: number | null
  /** Material `transparent` flag, when the object has a material. */
  transparent: boolean | null
  /** The object's `userData` (shown as "options" in the box). */
  userData: Record<string, unknown>
}

interface SceneElementDebugState {
  /** World-space point of the top-most hit, or null when nothing was hit. */
  clickPoint: [number, number, number] | null
  hits: DebugHit[]
}

const initialState: SceneElementDebugState = {
  clickPoint: null,
  hits: [],
}

/**
 * Transient (non-persisted) store shared between the in-scene click controller
 * and the out-of-canvas overlay box, which live on opposite sides of the R3F
 * canvas boundary and therefore cannot share React context.
 */
const useSceneElementDebugStore = create(
  combine(initialState, set => ({
    setResult: (clickPoint: [number, number, number] | null, hits: DebugHit[]) =>
      set({ clickPoint, hits }),
    clear: () => set({ clickPoint: null, hits: [] }),
  })),
)

export default useSceneElementDebugStore
